// services/collections.ts
import {
  apiClient,
  ApiError,
  ApiResponse,
  PaginatedApiResponse,
} from "@/lib/apiClient";
import { Category } from "@/types/models/category";

export interface FetchCategoriesOptions {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  filters?: {
    title?: string;
    description?: string;
  };
}

export interface SearchCategoriesOptions {
  query: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
}

export const getCategories = async ({
  page,
  pageSize,
  sortBy,
  sortOrder,
  filters,
}: FetchCategoriesOptions): Promise<PaginatedApiResponse<Category[]>> => {
  try {
    const params: Record<string, string> = {
      page: page.toString(),
      page_size: pageSize.toString(),
    };

    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    if (filters?.title) params.title = filters.title;
    if (filters?.description) params.description = filters.description;

    const data: PaginatedApiResponse<Category[]> = await apiClient.getPaginated(
      "/api/v1/admin/:storeId/collections",
      params,
    );
    return data;
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
export const putCategories = async ({
  collectionId,
  data,
}: {
  collectionId: string;
  data: Partial<Category>;
}): Promise<ApiResponse<Category>> => {
  try {
    const response: ApiResponse<Category> = await apiClient.put(
      `/api/v1/admin/:storeId/collections/${collectionId}`,
      data,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
export const postCategories = async ({
  data,
}: {
  data: Partial<Category>;
}): Promise<ApiResponse<Category>> => {
  try {
    const response: ApiResponse<Category> = await apiClient.post(
      `/api/v1/admin/:storeId/collections`,
      data,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
export const deleteCategories = async ({
  id,
}: {
  id: string;
}): Promise<ApiResponse<Category>> => {
  try {
    const response: ApiResponse<Category> = await apiClient.delete(
      `/api/v1/admin/:storeId/collections/${id}`,
    );
    return {
      data: response.data,
      status: response.status,
      message: response.message,
      success: response.success,
      valid: response.valid,
    };
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const searchCategories = async ({
  query,
  page,
  pageSize,
  sortBy,
  sortOrder,
}: SearchCategoriesOptions): Promise<PaginatedApiResponse<Category[]>> => {
  try {
    const params: Record<string, string> = {
      q: query,
    };

    if (page) params.page = page.toString();
    if (pageSize) params.page_size = pageSize.toString();
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;

    const data: PaginatedApiResponse<Category[]> = await apiClient.getPaginated(
      "/api/v1/admin/:storeId/collections/search",
      params,
    );
    return data;
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};

export const getCategoryById = async (
  categoryId: string,
): Promise<Category> => {
  try {
    const response: ApiResponse<Category> = await apiClient.get(
      `/api/v1/admin/:storeId/collections/${categoryId}`,
    );
    return response.data;
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(500, "UNKNOWN_ERROR", "An unexpected error occurred");
  }
};
